import { useEffect, useState, useCallback } from "react";
import { Eye, MagnifyingGlass, ArrowClockwise } from "@phosphor-icons/react";
import client from "../lib/api";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString([], { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "—");
const roleBadge = (r) => {
  const map = { admin: "bg-[#4A0000]/12 text-[#4A0000] border-[#4A0000]/25", employee: "bg-[#7E1119]/10 text-[#7E1119] border-[#7E1119]/25", agent: "bg-[#B08D57]/15 text-[#8A6D1B] border-[#B08D57]/40", photographer: "bg-[#2E5D34]/10 text-[#2E5D34] border-[#2E5D34]/25", pilot: "bg-[#3A5A7A]/10 text-[#3A5A7A] border-[#3A5A7A]/25" };
  return map[r] || "bg-[#FAF9F6] text-[#5C5A58] border-[#E9DDC6]";
};

export default function ImpersonationLog() {
  const [d, setD] = useState(null);
  const [q, setQ] = useState("");

  const load = useCallback(() => {
    client.get("/admin/audit", { params: { event: "impersonation_started", q: q || undefined, limit: 300 } })
      .then((r) => setD(r.data)).catch(() => setD({ events: [], total: 0, showing: 0 }));
  }, [q]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  if (!d) return <div className="py-16 text-center text-sm text-[#9A968E]" data-testid="viewas-log-loading">Loading view-as history…</div>;
  const events = d.events || [];

  return (
    <div className="space-y-4" data-testid="impersonation-log">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9A968E]" />
          <Input data-testid="viewas-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search by who or whom…" className="pl-9 bg-white border-[#E9DDC6]" />
        </div>
        <button data-testid="viewas-refresh" onClick={load} className="inline-flex items-center gap-1.5 text-sm text-[#6B6660] hover:text-[#4A0000] transition-colors">
          <ArrowClockwise size={15} /> Refresh
        </button>
        <span className="text-xs text-[#9A968E] ml-auto">Showing {events.length}{d.total ? ` of ${d.total} events` : ""}</span>
      </div>

      <div className="rounded-[8px] border border-[#E9DDC6] bg-white overflow-hidden">
        <div className="px-5 py-3.5 border-b border-[#F0ECE2]">
          <p className="text-sm font-semibold text-[#1A1715] flex items-center gap-2"><Eye size={16} weight="duotone" className="text-[#B08D57]" />View-as history</p>
          <p className="text-xs text-[#8A8681] mt-0.5">Every time an admin or employee views the portal as another user, it's recorded here.</p>
        </div>
        {events.length === 0 ? (
          <p className="px-5 py-12 text-center text-sm text-[#9A968E]" data-testid="viewas-empty">No view-as sessions {q ? "match your search" : "yet"}.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-[#9A968E] border-b border-[#F0ECE2]">
                  <th className="px-5 py-2.5 font-medium">Who</th>
                  <th className="px-3 py-2.5 font-medium">Viewed as</th>
                  <th className="px-3 py-2.5 font-medium hidden md:table-cell">Device</th>
                  <th className="px-5 py-2.5 font-medium text-right">When</th>
                </tr>
              </thead>
              <tbody data-testid="viewas-rows">
                {events.map((e, i) => (
                  <tr key={i} data-testid={`viewas-row-${i}`} className="border-b border-[#FAF9F6]">
                    <td className="px-5 py-3">
                      <span className="font-medium text-[#2C2626]">{e.admin_name || "—"}</span>
                      {e.admin_role && <Badge className={`ml-2 capitalize ${roleBadge(e.admin_role)}`}>{e.admin_role}</Badge>}
                    </td>
                    <td className="px-3 py-3">
                      <span className="text-[#2C2626]">{e.target_name || "—"}</span>
                      {e.target_role && <Badge className={`ml-2 capitalize ${roleBadge(e.target_role)}`}>{e.target_role}</Badge>}
                    </td>
                    <td className="px-3 py-3 text-xs text-[#9A968E] hidden md:table-cell truncate max-w-[260px]" title={e.user_agent || ""}>{e.ip || "—"}</td>
                    <td className="px-5 py-3 text-right text-[#8A8681] whitespace-nowrap">{fmt(e.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
